import { ChangeEvent, useState } from "react";
import { useDeleteCandidates } from "../../../../App/Queries/CandidateQueries";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";
import { ICandidate, IDeleteCandidateError } from "../../../../App/Typings/Candidate";
import { IClient } from "../../../../App/Typings/Client";

export enum DeleteType {
  SearchAndDelete = "SearchAndDelete",
  DirectDelete = "DirectDelete",
}

/**
 * @description Initializes state and returns an object representing it
 *
 * @returns An object representing the state of the ManageCandidate screen
 */
export function DeleteCandidateState(selectedClient?: IClient) {
  //get selected Client from Redux
  const user = useCurrentUser();
  const { displaySuccess, displayError } = useNotification();

  //track if data is loading
  const [deleteType, setDeleteType] = useState<DeleteType>(DeleteType.SearchAndDelete);

  //add candidate modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedCandidate, setSelectedCandidate] = useState<ICandidate>({} as ICandidate);
  const [candidatesToDelete, setCandidatesToDelete] = useState<string[]>([]);
  const [failedDeletes, setFailedDeletes] = useState<IDeleteCandidateError[]>([]);
  const [responseMessage, setResponseMessage] = useState<string>("");

  //Selected Candidates For Messages
  const [candidateSelectedList, setCandidateSelectedList] = useState<string[]>([]);

  const { mutateAsync: deleteCandidates, isLoading: isDeleteCandidatesLoading } = useDeleteCandidates();
  const handleChangeDeleteType = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDeleteType(ev.target.value as DeleteType);
    setCandidatesToDelete([]);
  };

  const handleDeleteCandidates = useDebouncedHandler(async () => {
    if (candidatesToDelete.length === 0) {
      return;
    }
    await deleteCandidates(
      {
        clientId: selectedClient?.clientId ?? "",
        candidateIds: candidatesToDelete,
      },
      {
        onSettled: (response, error) => {
          if (error || !response) {
            displayError("Error deleting candidates, please try again");
            return;
          }

          setResponseMessage(response.message);
          setFailedDeletes(response.errors);

          if (!response.success) {
            const message = response.message
              ? `Error deleting candidates: ${response.message}`
              : "Error deleting candidates: please try again";
            displayError(message);
            if (response.errors.length === 0) {
              setIsDeleteModalOpen(false);
            }
            return;
          }
          if (response.success) {
            displaySuccess("Candidates deleted successfully");
          }
          if (response.success && response.errors.length === 0) {
            setCandidatesToDelete([]);
            setIsDeleteModalOpen(false);
          }
        },
      },
    );
  }, 300);

  const handleCloseDeleteModalWithFailedDeletes = () => {
    setFailedDeletes([]);
    setCandidatesToDelete([]);
    setIsDeleteModalOpen(false);
  };

  return {
    effects: {
      handleChangeDeleteType,
      handleDeleteCandidates,
      handleCloseDeleteModalWithFailedDeletes,
    },
    getters: {
      candidatesToDelete,
      deleteType,
      failedDeletes,
      selectedClient,
      user,
      isDeleteModalOpen,
      isDeleteCandidatesLoading,
      selectedCandidate,
      candidateSelectedList,
      responseMessage,
    },
    setters: {
      setCandidatesToDelete,
      setFailedDeletes,
      setIsDeleteModalOpen,
      setSelectedCandidate,
      setCandidateSelectedList,
    },
  };
}

export type DeleteCandidateStateType = ReturnType<typeof DeleteCandidateState>;
